import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { getOrganizationMutationKey, getOrganizationResourcesMutationKey, getOrganizationUsersMutationKey } from '.';
import { organizationService } from '../../application/OrganizationService';
import { useUserContext } from '../../context/user/context';
import { OrganizationUserRoles } from '../../domain/organization/OrganizationUserRoles';
import useErrorMiddleware from '../errorMiddleware/useErrorMiddleware';
import { useRetrieveActiveProject } from '../project/useRetrieveActiveProject';
import { useRetrieveFlatProjects } from '../project/useRetrieveFlatProjects';
import { useShowSnackbarMessage } from '../snackbarMessage/useShowSnackbarMessage';

interface UseCreateOrganizationUserProps {
  email: string;
  firstName: string;
  lastName: string;
  role: OrganizationUserRoles;
  projects: string[];
}

type UseCreateOrganizationUserResponseProps = ServiceResponse<
  ({ email, firstName, lastName, role, projects }: UseCreateOrganizationUserProps) => void,
  void
>;

// Hooks act as 'Adapter' layer.
export const useCreateOrganizationUser = (): UseCreateOrganizationUserResponseProps => {
  const queryClient = useQueryClient();
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const { userState } = useUserContext();
  const { run: refetchProjects } = useRetrieveFlatProjects({});
  const { run: refetchActiveProject } = useRetrieveActiveProject();

  const { isLoading, isSuccess, isError, mutate } = useMutation(
    ({ email, firstName, lastName, role, projects }: UseCreateOrganizationUserProps): Promise<void> => {
      return organizationService.createUser({
        email,
        firstName,
        lastName,
        role,
        projects,
        language: userState.user!.language,
      });
    },
    {
      onSuccess: () => {
        refetchActiveProject();
        refetchProjects();
        queryClient.invalidateQueries(getOrganizationMutationKey());
        queryClient.invalidateQueries(getOrganizationUsersMutationKey());
        queryClient.invalidateQueries(getOrganizationResourcesMutationKey());
      },
      onError: (err: any) => {
        console.info(`Create Organization User Error: `, err?.response);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }
        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  return {
    error: isError,
    isLoading: isLoading,
    success: isSuccess,
    run: ({ email, firstName, lastName, role, projects }: UseCreateOrganizationUserProps) =>
      mutate({ email, firstName, lastName, role, projects }),
  };
};
